import apiRequest from 'api/apiRequest';
import endpoints from 'api/endpoints';
import cn from 'classnames';
import { HttpMethods } from 'api/types';
import OpPlusLoaderWrapper from 'components/Common/OpPlusLoaderWrapper/OpPlusLoaderWrapper';
import LandingPageBuilder from 'components/LandingPage/Factory/LandingPageBuilder';
import { LandingPageProvider } from 'context/LandingPageContext';
import useActiveRoute from 'hooks/useActiveRoute';
import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mapParameters } from 'routes';
import { AppDispatch } from 'store';
import getAddingOpPlusToBasket from 'store/app/selector/getAddingOpPlusToBasket';
import { setTrustpilotDataAction } from 'store/app/slice';
import { TrustpilotData } from 'store/app/types';
import getLandingPageData from 'store/landing/selector/getLandingPageData';
import { setLandingPageCategoryId, setLandingPageData } from 'store/landing/slice';
import { LandingPageData } from 'store/landing/types';
import SSRParams from 'utils/class/SSRParams';
import gtmSendFunnelStepEvent, { FUNNEL_STEPS } from 'utils/gtm/gtmSendFunnelStepEvent';
import OpPlusAddedNotification from 'components/Common/OpPlusAddedNotification/OpPlusAddedNotification';
import styles from './LandingPage.module.scss';

type GetLandingPageParams = {
  categoryName?: string;
  categoryId?: string;
};

const fetchTrustpilotData = async (dispatch: AppDispatch) => {
  const data = await apiRequest<TrustpilotData>(endpoints.trustpilotV1Get, {}, HttpMethods.POST, 'data');
  dispatch(setTrustpilotDataAction(data));
};

const fetchLandingPageData = async (dispatch: AppDispatch, params: GetLandingPageParams) => {
  const lp = await apiRequest<LandingPageData>(endpoints.landingpageV1Get, params, 'POST', 'data');
  if (lp.uniqueSellingPoints) {
    await fetchTrustpilotData(dispatch);
  }
  if (params.categoryId) {
    dispatch(setLandingPageCategoryId(+params.categoryId));
  }
  dispatch(setLandingPageData(lp));
  gtmSendFunnelStepEvent(FUNNEL_STEPS.landing, `/${lp.pageId}`).then();
};

const LandingPage: FC = () => {
  const dispatch = useDispatch();
  const route = useActiveRoute();
  const data = useSelector(getLandingPageData);
  const addOpPlusInProgress = useSelector(getAddingOpPlusToBasket);

  useEffect(() => {
    if (route?.mappingId !== data?.urlMapping) {
      const apiParams = mapParameters(route?.parameters || []);
      const params = {
        categoryId: apiParams.get('categoryId'),
        categoryName: apiParams.get('categoryName'),
      };
      fetchLandingPageData(dispatch, params).then();
    }
  }, [route?.mappingId]);

  if (!data) return null;

  return (
    <LandingPageProvider>
      {addOpPlusInProgress && <OpPlusLoaderWrapper />}
      <LandingPageBuilder landingPageData={data} className={cn({ [styles.blockClicks]: addOpPlusInProgress })} />
      <OpPlusAddedNotification />
    </LandingPageProvider>
  );
};

export const fetchLandingPageInitialData = (apiParams: GetLandingPageParams) => (params: SSRParams) => {
  return [fetchLandingPageData(params.dispatch, apiParams)];
};

export default LandingPage;
